/**
 * useRandomCapture — Isolated random webcam + screen capture monitoring hook.
 *
 * DESIGN PRINCIPLES
 * ─────────────────
 * • Backend-controlled timing only. Frontend polls and reacts; never predicts.
 * • Fully async — no UI thread blocking.
 * • All capture, compression, upload, and retry logic lives here.
 * • Zero knowledge of existing proctoring modules.
 *
 * USAGE
 * ─────
 * const { captureCount, screenShareActive, requestScreenShare } = useRandomCapture({
 *   interviewId,   // string  — from URL params
 *   videoRef,      // React ref pointing to <video> element (webcam stream)
 *   isActive,      // boolean — true while interview is in questioning stage
 *   apiBaseUrl,    // string  — e.g. "https://api.exihire.com"
 *   onViolation,   // (type, meta) => void — called for proctoring events
 * });
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

// ─── Constants ───────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS    = 4800;   // ~5 s, non-round to avoid synchronisation
const RETRY_INTERVAL_MS   = 30_000; // retry failed uploads every 30 s
const MAX_RETRY_ATTEMPTS  = 3;
const RETRY_BASE_DELAY_MS = 1_000;
const CANVAS_MAX_DIM      = 1280;   // px — limits JPEG size
const JPEG_QUALITY        = 0.75;   // keeps under 200 KB for most frames
const UPLOAD_TIMEOUT_MS   = 15_000; // per upload attempt

// ─── Image utilities ──────────────────────────────────────────────────────────

function captureFrameFromVideo(videoEl) {
  if (!videoEl || videoEl.readyState < 2) return null;

  const w = videoEl.videoWidth  || videoEl.width  || 640;
  const h = videoEl.videoHeight || videoEl.height || 480;

  const scale = Math.min(1, CANVAS_MAX_DIM / Math.max(w, h));
  const cw    = Math.round(w * scale);
  const ch    = Math.round(h * scale);

  const canvas = document.createElement('canvas');
  canvas.width  = cw;
  canvas.height = ch;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(videoEl, 0, 0, cw, ch);
  return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
}

function captureFrameFromStream(stream) {
  if (!stream || !stream.active) return Promise.resolve(null);

  return new Promise(resolve => {
    const video = document.createElement('video');
    video.muted        = true;
    video.playsInline  = true;
    video.srcObject    = stream;

    let settled = false;
    const finish = (blob) => {
      if (settled) return;
      settled = true;
      video.srcObject = null;
      resolve(blob);
    };

    video.onloadeddata = () => {
      // Wait for the browser to paint the first frame before drawing.
      // Without requestAnimationFrame the canvas can capture a blank frame
      // because the GPU hasn't composited the stream texture yet.
      requestAnimationFrame(() => {
        const w = video.videoWidth  || 1280;
        const h = video.videoHeight || 720;

        const scale = Math.min(1, CANVAS_MAX_DIM / Math.max(w, h));
        const cw    = Math.round(w * scale);
        const ch    = Math.round(h * scale);

        const canvas = document.createElement('canvas');
        canvas.width  = cw;
        canvas.height = ch;

        const ctx = canvas.getContext('2d');
        if (ctx) ctx.drawImage(video, 0, 0, cw, ch);

        canvas.toBlob(blob => finish(blob), 'image/jpeg', JPEG_QUALITY);
      });
    };

    video.onerror = () => finish(null);
    video.play().catch(() => finish(null));
    setTimeout(() => finish(null), 5000);
  });
}

// ─── Upload utility ───────────────────────────────────────────────────────────

/**
 * Direct PUT to S3 presigned URL.
 * Returns { ok: true } or { ok: false, reason: string }.
 * Does NOT send Content-Type header — only 'host' is in X-Amz-SignedHeaders,
 * so adding Content-Type would cause a separate CORS preflight for it.
 */
async function uploadBlobToS3(presignedUrl, blob, signal) {
  try {
    const res = await fetch(presignedUrl, {
      method: 'PUT',
      body:   blob,
      signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      return { ok: false, reason: `HTTP ${res.status}: ${text.slice(0, 200)}` };
    }
    return { ok: true };
  } catch (err) {
    const reason = err.name === 'AbortError' ? 'timeout' : (err.message || 'network_error');
    return { ok: false, reason };
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export default function useRandomCapture({
  interviewId,
  videoRef,
  isActive,
  apiBaseUrl,
  onViolation,
}) {
  const [captureCount,        setCaptureCount]        = useState(0);
  const [screenShareActive,   setScreenShareActive]   = useState(false);
  const [screenShareIsMonitor, setScreenShareIsMonitor] = useState(false);

  const screenStreamRef  = useRef(null);
  const failedQueueRef   = useRef([]);   // [{ captureId, captureType, blob, attempts, firstFailReason }]
  const pollTimerRef     = useRef(null);
  const retryTimerRef    = useRef(null);
  const isMountedRef     = useRef(true);
  const isCapturingRef   = useRef(false);
  const captureCountRef  = useRef(0);

  const apiRef = useRef(apiBaseUrl);
  useEffect(() => { apiRef.current = apiBaseUrl; }, [apiBaseUrl]);

  const interviewIdRef = useRef(interviewId);
  useEffect(() => { interviewIdRef.current = interviewId; }, [interviewId]);

  // ── Violation reporter ─────────────────────────────────────────────────────

  const reportViolation = useCallback(async (eventType, severity, meta = {}) => {
    if (!isMountedRef.current) return;
    try {
      if (typeof onViolation === 'function') {
        onViolation(eventType, { severity, ...meta });
      }
      await axios.post(
        `${apiRef.current}/random-capture/event`,
        { interviewId: interviewIdRef.current, eventType, severity, meta },
        { withCredentials: true, timeout: 5000 }
      );
    } catch { /* violation reporting must never throw */ }
  }, [onViolation]);

  // ── Screen share ───────────────────────────────────────────────────────────

  /**
   * Must be called directly from a user-gesture handler (button onClick).
   * Stores the stream; the hook captures frames from it on each tick.
   */
  const requestScreenShare = useCallback(async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      console.warn('[random-capture] Screen share: getDisplayMedia not supported in this browser');
      return { granted: false, reason: 'not_supported' };
    }
    try {
      console.log('[random-capture] Requesting screen share permission...');
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always', displaySurface: 'monitor' },
        audio: false,
      });

      // Validate that the user selected the entire screen (not a browser tab or window).
      // displaySurface is 'monitor' for entire screen, 'browser' for a tab, 'window' for an app window.
      const track = stream.getVideoTracks()[0];
      const displaySurface = track?.getSettings()?.displaySurface;
      const isMonitor = !displaySurface || displaySurface === 'monitor';

      if (!isMonitor) {
        console.warn(`[random-capture] Screen share rejected: displaySurface="${displaySurface}" (must be "monitor")`);
        stream.getTracks().forEach(t => t.stop());
        setScreenShareActive(false);
        setScreenShareIsMonitor(false);
        reportViolation('SCREEN_SHARE_WRONG_SURFACE', 'MEDIUM', { displaySurface });
        return { granted: false, reason: 'not_monitor' };
      }

      screenStreamRef.current = stream;
      setScreenShareActive(true);
      setScreenShareIsMonitor(true);
      console.log('[random-capture] Screen share: permission granted (monitor)');

      // Detect user stopping screen share
      track?.addEventListener('ended', () => {
        if (!isMountedRef.current) return;
        console.warn('[random-capture] Screen share: user stopped sharing');
        screenStreamRef.current = null;
        setScreenShareActive(false);
        setScreenShareIsMonitor(false);
        reportViolation('SCREEN_SHARE_LOST', 'HIGH', {
          interviewId: interviewIdRef.current,
          reason: 'user_stopped',
        });
      });

      return { granted: true, isMonitor: true };
    } catch (err) {
      const reason = err.name === 'NotAllowedError' ? 'permission_denied' : (err.name || 'unknown');
      console.warn(`[random-capture] Screen share: failed (${reason})`);
      reportViolation('SCREEN_SHARE_UNAVAILABLE', 'MEDIUM', { reason });
      return { granted: false, reason };
    }
  }, [reportViolation]);

  // ── Confirm upload (backend call) ──────────────────────────────────────────

  const safeConfirmUpload = useCallback(async (captureId, captureType, s3Key) => {
    try {
      await axios.post(
        `${apiRef.current}/random-capture/confirm-upload`,
        {
          interviewId: interviewIdRef.current,
          captureId,
          captureType,
          s3Key,
          timestamp: new Date().toISOString(),
        },
        { withCredentials: true, timeout: 5000 }
      );
      captureCountRef.current += 1;
      if (isMountedRef.current) setCaptureCount(captureCountRef.current);
      console.log(`[random-capture] DB updated: captureId=${captureId} type=${captureType} status=uploaded`);
    } catch (err) {
      console.error(`[random-capture] confirm-upload call failed captureId=${captureId}:`, err.message);
    }
  }, []);

  // ── Mark failed (backend call) ─────────────────────────────────────────────

  const safeMarkFailed = useCallback(async (captureId, captureType, reason) => {
    try {
      await axios.post(
        `${apiRef.current}/random-capture/mark-failed`,
        {
          interviewId: interviewIdRef.current,
          captureId,
          captureType,
          reason,
        },
        { withCredentials: true, timeout: 5000 }
      );
      console.warn(`[random-capture] Capture marked failed: captureId=${captureId} type=${captureType} reason=${reason}`);
    } catch { /* best-effort */ }
  }, []);

  // ── Core capture + upload pipeline ────────────────────────────────────────

  const executeCaptureForId = useCallback(async (captureId, captureTypes) => {
    const iid = interviewIdRef.current;
    const api = apiRef.current;

    console.log(`[random-capture] Capture triggered: captureId=${captureId} types=${captureTypes.join(',')}`);

    // 1. Capture frames
    const blobs = {};
    await Promise.all(
      captureTypes.map(async type => {
        if (type === 'webcam' && videoRef?.current) {
          const blob = await captureFrameFromVideo(videoRef.current);
          if (blob) {
            blobs[type] = blob;
            console.log(`[random-capture] Webcam frame captured: ${(blob.size / 1024).toFixed(1)} KB`);
          } else {
            console.warn('[random-capture] Webcam capture failed: no frame');
          }
        } else if (type === 'screen') {
          const stream = screenStreamRef.current;
          if (stream?.active) {
            const blob = await captureFrameFromStream(stream);
            if (blob) {
              blobs[type] = blob;
              console.log(`[random-capture] Screen frame captured: ${(blob.size / 1024).toFixed(1)} KB`);
            } else {
              console.warn('[random-capture] Screen capture failed: no frame from stream');
            }
          } else {
            await reportViolation('SCREEN_SHARE_UNAVAILABLE', 'MEDIUM', { interviewId: iid, captureId });
          }
        }
      })
    );

    if (Object.keys(blobs).length === 0) {
      console.error(`[random-capture] No frames captured for captureId=${captureId}`);
      await reportViolation('RANDOM_CAPTURE_DENIED', 'HIGH', { interviewId: iid, captureId, reason: 'no_frames' });
      return;
    }

    // 2. Get presigned URLs from backend
    let presignedResult = null;
    try {
      console.log(`[random-capture] Requesting presigned URL: captureId=${captureId}`);
      const res = await axios.post(
        `${api}/random-capture/presigned-url`,
        { interviewId: iid, captureId, captureTypes: Object.keys(blobs) },
        { withCredentials: true, timeout: 8000 }
      );
      presignedResult = res.data;
      console.log(`[random-capture] Presigned URL received: s3Available=${presignedResult?.s3Available}`);
    } catch (err) {
      console.error(`[random-capture] Presigned URL request failed: ${err.message} — queuing for retry`);
      for (const [type, blob] of Object.entries(blobs)) {
        failedQueueRef.current.push({ captureId, captureType: type, blob, attempts: 0, firstFailReason: err.message });
      }
      return;
    }

    if (!presignedResult?.s3Available) {
      // S3 not configured — confirm without S3 key (audit trail only)
      for (const type of Object.keys(blobs)) {
        await safeConfirmUpload(captureId, type, null);
      }
      return;
    }

    // 3. Upload directly to S3 using presigned URL
    const { urls, s3Keys } = presignedResult;

    for (const [type, blob] of Object.entries(blobs)) {
      const url   = urls?.[type];
      const s3Key = s3Keys?.[type];

      if (!url) {
        console.warn(`[random-capture] No presigned URL for type=${type} — queuing`);
        failedQueueRef.current.push({ captureId, captureType: type, blob, attempts: 0, firstFailReason: 'no_url' });
        continue;
      }

      console.log(`[random-capture] Upload started: captureId=${captureId} type=${type}`);
      const controller = new AbortController();
      const timer      = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
      let   result     = { ok: false, reason: 'unknown' };

      try {
        result = await uploadBlobToS3(url, blob, controller.signal);
      } finally {
        clearTimeout(timer);
      }

      if (result.ok) {
        console.log(`[random-capture] Upload success: captureId=${captureId} type=${type} s3_key=${s3Key}`);
        await safeConfirmUpload(captureId, type, s3Key);
      } else {
        console.error(`[random-capture] Upload failed: captureId=${captureId} type=${type} reason=${result.reason}`);
        failedQueueRef.current.push({ captureId, captureType: type, blob, attempts: 0, firstFailReason: result.reason });
      }
    }
  }, [videoRef, reportViolation, safeConfirmUpload]);

  // ── Poll loop ──────────────────────────────────────────────────────────────

  const poll = useCallback(async () => {
    if (!isMountedRef.current || isCapturingRef.current) return;

    try {
      const res = await axios.get(
        `${apiRef.current}/random-capture/tick`,
        {
          params:          { interviewId: interviewIdRef.current },
          withCredentials: true,
          timeout:         5000,
        }
      );

      const data = res.data;
      if (!data?.shouldCapture) return;

      isCapturingRef.current = true;
      try {
        await executeCaptureForId(data.captureId, data.captureTypes || ['webcam', 'screen']);
      } finally {
        isCapturingRef.current = false;
      }

    } catch { /* network blip — next tick will retry */ }
  }, [executeCaptureForId]);

  // ── Failed upload retry ────────────────────────────────────────────────────

  const retryFailedUploads = useCallback(async () => {
    if (!isMountedRef.current) return;

    const queue = failedQueueRef.current;
    if (!queue.length) return;

    console.log(`[random-capture] Retrying ${queue.length} failed upload(s)`);
    const remaining = [];

    for (const item of queue) {
      if (item.attempts >= MAX_RETRY_ATTEMPTS) {
        console.error(`[random-capture] Giving up after ${MAX_RETRY_ATTEMPTS} retries: captureId=${item.captureId} type=${item.captureType} firstFailReason=${item.firstFailReason}`);
        // Mark as failed in the DB so status is not stuck at 'uploading'
        await safeMarkFailed(item.captureId, item.captureType, `max_retries:${item.firstFailReason || 'unknown'}`);
        await reportViolation('RANDOM_CAPTURE_FAILED', 'MEDIUM', {
          interviewId: interviewIdRef.current,
          captureId:   item.captureId,
          captureType: item.captureType,
          attempts:    item.attempts,
        });
        continue;
      }

      item.attempts += 1;
      const delay = RETRY_BASE_DELAY_MS * Math.pow(2, item.attempts - 1);
      console.log(`[random-capture] Retry attempt ${item.attempts}/${MAX_RETRY_ATTEMPTS}: captureId=${item.captureId} type=${item.captureType} delay=${delay}ms`);
      await new Promise(r => setTimeout(r, delay));

      try {
        const res = await axios.post(
          `${apiRef.current}/random-capture/presigned-url`,
          { interviewId: interviewIdRef.current, captureId: item.captureId, captureTypes: [item.captureType] },
          { withCredentials: true, timeout: 8000 }
        );

        const { urls, s3Keys, s3Available } = res.data || {};

        if (!s3Available) {
          await safeConfirmUpload(item.captureId, item.captureType, null);
          continue;
        }

        const url   = urls?.[item.captureType];
        const s3Key = s3Keys?.[item.captureType];

        if (!url) { remaining.push(item); continue; }

        const controller = new AbortController();
        const timer      = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
        let   result     = { ok: false, reason: 'unknown' };

        try {
          result = await uploadBlobToS3(url, item.blob, controller.signal);
        } finally {
          clearTimeout(timer);
        }

        if (result.ok) {
          console.log(`[random-capture] Retry ${item.attempts} success: captureId=${item.captureId} type=${item.captureType}`);
          await safeConfirmUpload(item.captureId, item.captureType, s3Key);
        } else {
          console.warn(`[random-capture] Retry ${item.attempts} failed: captureId=${item.captureId} reason=${result.reason}`);
          remaining.push(item);
        }
      } catch (err) {
        console.warn(`[random-capture] Retry ${item.attempts} network error: ${err.message}`);
        remaining.push(item);
      }
    }

    failedQueueRef.current = remaining;
  }, [reportViolation, safeConfirmUpload, safeMarkFailed]);

  // ── Lifecycle effects ──────────────────────────────────────────────────────

  useEffect(() => {
    isMountedRef.current = true;

    if (!isActive) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
      return;
    }

    if (pollTimerRef.current) return;

    const jitter = Math.floor(Math.random() * 2000);
    const initTimer = setTimeout(() => {
      if (!isMountedRef.current) return;
      poll();
      pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    }, jitter);

    return () => {
      clearTimeout(initTimer);
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    if (!isActive) {
      clearInterval(retryTimerRef.current);
      retryTimerRef.current = null;
      return;
    }

    retryTimerRef.current = setInterval(retryFailedUploads, RETRY_INTERVAL_MS);

    return () => {
      clearInterval(retryTimerRef.current);
      retryTimerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearInterval(pollTimerRef.current);
      clearInterval(retryTimerRef.current);
      pollTimerRef.current  = null;
      retryTimerRef.current = null;

      if (screenStreamRef.current) {
        try { screenStreamRef.current.getTracks().forEach(t => t.stop()); } catch { /* ignore */ }
        screenStreamRef.current = null;
      }

      failedQueueRef.current = [];
    };
  }, []);

  return { captureCount, screenShareActive, screenShareIsMonitor, requestScreenShare };
}
